import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Link, useParams, useLocation } from 'react-router-dom';

import {
  Gift,
  WatsonHealthImageAvailabilityLocal,
  Calendar,
  Link as LinkIcon,
  StarFilled,
} from '@carbon/icons-react';

import CustomDate from '../../misc/CustomDate';
import { FormattedMessageWrappedInSpan } from '../../misc';
import { includesCertificate, includesVideo, maybeExtractCombinedBib } from '../../../utils/common';

const EventBar = ({
  event,
  onExtrasClick,
  bestOfAvailable,
  onExpandBestof,
}) => {
  const params = useParams();
  const {pathname} = useLocation();

  // for account page, we want to redirect user to smartsearch results
  const renderLinkToEventPage = () => {
    if (pathname.includes('account') && params.bibnumber) {
      return `/event/${event.sgId}/smartsearch/${maybeExtractCombinedBib(
        params.bibnumber
      )}`;
    } else return `/event/${event.sgId}`;
  };

  const includesExtras =
    (event.voucherCampaigns && event.voucherCampaigns.length > 0) ||
    includesCertificate(event.products) ||
    includesVideo(event.products);

  return (
    <div className="event-bar">
      <section className="event-bar_logo">
        <Link to={renderLinkToEventPage()}>
          <img className="event-bar_logo_image" src={event.banner} />
        </Link>
      </section>
      <section className="event-bar_name">
        <Link to={`/event/${event.sgId}`}>
          <span className="typography uppercase bold-text typography-title_medium typography-title_medium-sm">
            {event.name}
          </span>
        </Link>
      </section>
      <section className="event-bar_info">
        {event.location && (
          <span className="event-bar_info_event-property typography typography-caption_small typography-caption_small-sm">
            <WatsonHealthImageAvailabilityLocal size={16} /> {event.location}
          </span>
        )}
        {event.beginsAt && (
          <span className="event-bar_info_event-property typography typography-caption_small typography-caption_small-sm">
            <Calendar size={16} /> <CustomDate value={event.beginsAt} />
          </span>
        )}
        {event.hostHomepageUrl && (
          <a target="_blank" className='event-bar_info_link-wrapper' href={event.hostHomepageUrl}>
            <span className="event-bar_info_event-property typography typography-caption_small typography-caption_small-sm">
              <LinkIcon size={16} />
              {event.hostHomepageName ? (
                <span className='event-bar_info_event-organizer'>{event.hostHomepageName}</span>
              ) : (
                <FormattedMessageWrappedInSpan
                  id="eventBar.organizerWeb"
                  defaultMessage="Website"
                  className="typography-caption_medium-sm"
                />
              )}
            </span>
          </a>
        )}
      </section>
      <section className="event-bar_actions">
        {includesExtras && onExtrasClick && (
          <button
            className="secondary-new event-bar_action-btn btn"
            onClick={() => onExtrasClick && onExtrasClick()}
          >
            <Gift size={24} />
            <FormattedMessageWrappedInSpan
              id="eventBar.seeExtras"
              defaultMessage="See Extras"
              className="typography-caption_medium-sm"
            />
          </button>
        )}
        {bestOfAvailable && (
          <button
            className="primary-new event-bar_action-btn btn"
            onClick={() => onExpandBestof && onExpandBestof()}
          >
            <StarFilled size={24} />
            <FormattedMessageWrappedInSpan
              id="eventBar.seeBestOf"
              defaultMessage="See Best-of"
              className="typography-caption_medium-sm"
            />
          </button>
        )}
      </section>
    </div>
  );
};

EventBar.propTypes = {
  eventInfo: PropTypes.object,
  onExtrasClick: PropTypes.func,
  bestOfAvailable: PropTypes.bool,
  onExpandBestof: PropTypes.func,
};

export { EventBar };

export default createFragmentContainer(EventBar, {
  event: graphql`
    fragment EventBar_event on Event {
      participantUser
      hostResultUrl
      beginsAt
      name
      sgId
      banner
      hostHomepageUrl
      hostHomepageName
      location
      voucherCampaigns {
        id
      }
      products {
        type
      }
    }
  `,
});
